import {
  Dispatch,
  SetStateAction,
  useContext,
  useEffect,
  useState,
} from "react";

import { useNavigate } from "react-router-dom";

import {
  ActionIcon,
  Badge,
  Button,
  Divider,
  Modal,
  Paper,
  Text,
  TextInput,
} from "@mantine/core";
import { useDisclosure, useViewportSize } from "@mantine/hooks";

import { useForm } from "@mantine/form";
import {
  IconCheck,
  IconChevronDown,
  IconChevronUp,
  IconEdit,
} from "@tabler/icons-react";

import { motion } from "framer-motion";

import {
  BrandImages,
  Colors,
  HttpSuccessCodes,
  MinimumLengthForCouponCode,
  PurchasePeriods,
} from "../../Lib/Data";
import {
  DefaultErrorNotification,
  DefaultSuccessNotification,
  getDiscountedValue,
  getFinancialValueFromNumeric,
  trimString,
} from "../../Lib/Methods";
import {
  googleOneStorageProps,
  iCloudStorageProps,
  PossiblePeriods,
  PossibleServices,
} from "../../Lib/Types";

import { Endpoints } from "../../Lib/Endpoints";
import { PerformRequest } from "../../Lib/usePerformRequest";

import {
  CouponValidationResponse,
  NonPaginatedResponse,
} from "../../Lib/Responses";
import { AppContext } from "../../Pages/Wrapper";

import "./styles.scss";
interface MyCartSectionProps {
  selectedServices?: PossibleServices[];
  iCloudStorage: iCloudStorageProps | null;
  googleOneStorage: googleOneStorageProps | null;
  selectedPeriod: PossiblePeriods;
  setSelectedPeriod: Dispatch<SetStateAction<PossiblePeriods>>;
  totalSaved: number;
  currentSingleService?: PossibleServices;
  totalAmount: number;
  hideShadow?: boolean;
  handlePaymentSuccess?: () => any;
  subscriptionID?: string;
  isRenewalPayment?: boolean;
}
const MyCartSection = ({
  selectedServices,
  iCloudStorage,
  selectedPeriod,
  setSelectedPeriod,
  totalSaved,
  currentSingleService,
  totalAmount,
  hideShadow,
  googleOneStorage,
  handlePaymentSuccess,
  subscriptionID,
  isRenewalPayment,
}: MyCartSectionProps) => {
  const context = useContext(AppContext);
  const theme = context?.theme ?? "light";
  const navigate = useNavigate();

  const { width: browserCurrentWidth } = useViewportSize();
  const isSmallScreen = browserCurrentWidth < 1070;

  const [isCartShowing, { open: OpenCart, close: CloseCart }] =
    useDisclosure(false);
  const height = isSmallScreen ? (isCartShowing ? "auto" : "auto") : "auto";

  const isBundle =
    selectedServices && selectedServices.length > 0 ? true : false;

  const isService =
    currentSingleService && currentSingleService.length > 0 ? true : false;

  const isSelectedServiceOrBundle = isBundle || isService;

  const assumingCurrentSingleServiceExistsSoImage = BrandImages.retrieveImage(
    currentSingleService as PossibleServices,
    theme,
  );

  const showImages =
    (selectedServices && selectedServices.length > 0) ||
    (assumingCurrentSingleServiceExistsSoImage &&
      assumingCurrentSingleServiceExistsSoImage.length > 0);

  const [
    isPaymentModalOpened,
    { open: OpenPaymentModal, close: ClosePaymentModal },
  ] = useDisclosure(false);

  const [couponVerificationStatus, setCouponVerificationStatus] = useState<
    "unset" | "verified" | "verifying" | "error" | "unverified"
  >("unset");
  const couponForm = useForm({
    initialValues: {
      coupon: "",
      discount: 0,
    },
    validate: {
      coupon: (value) =>
        value && value.length > 0
          ? value.length >= MinimumLengthForCouponCode
            ? null
            : true
          : null,
    },
  });

  useEffect(() => {
    if (couponForm.values.coupon.length === 0) {
      setCouponVerificationStatus("unverified");
    }
  }, [couponForm.values.coupon]);
  const ValidateCoupon = async () => {
    const { hasErrors: formHasErrors } = couponForm.validate();
    if (!formHasErrors) {
      const { coupon } = couponForm.values;
      if (coupon.length >= MinimumLengthForCouponCode) {
        setCouponVerificationStatus("verifying");
        const r = await PerformRequest<
          NonPaginatedResponse<CouponValidationResponse>
        >({
          method: "POST",
          route: Endpoints.GetValidateCoupon(),
          data: {
            coupon: trimString(coupon).toUpperCase(),
          },
          callback: () => {
            setCouponVerificationStatus("error");
          },
        });
        if (r) {
          if (r.status === 200) {
            DefaultSuccessNotification("Discount added!");
            setCouponVerificationStatus("verified");
            couponForm.setFieldValue("discount", r.data.data.value);
          } else if (r.status === 404) {
            setCouponVerificationStatus("error");
            DefaultErrorNotification("Code does not exist!");
          } else {
            DefaultErrorNotification("Invalid or expired code!");
            setCouponVerificationStatus("error");
          }
        } else {
          setCouponVerificationStatus("error");
        }
      }
    }
  };

  const [
    isPaymentLoading,
    { open: StartPaymentLoading, close: StopPaymentLoading },
  ] = useDisclosure(false);

  const InitiatePayment = async () => {
    StartPaymentLoading();

    let checkoutPayloadForNewPayment: any = {
      services: isService ? [currentSingleService] : selectedServices,
      duration: selectedPeriod,
    };
    if (iCloudStorage) {
      checkoutPayloadForNewPayment = {
        ...checkoutPayloadForNewPayment,
        iCloudStorage: iCloudStorage,
      };
    }
    if (googleOneStorage) {
      checkoutPayloadForNewPayment = {
        ...checkoutPayloadForNewPayment,
        googleOneStorage: googleOneStorage,
      };
    }
    let checkoutPaymentForRenewals: any = {
      id: subscriptionID,
      duration: selectedPeriod,
    };
    if (iCloudStorage) {
      checkoutPaymentForRenewals = {
        ...checkoutPaymentForRenewals,
        iCloudStorage: iCloudStorage,
      };
    }
    if (googleOneStorage) {
      checkoutPaymentForRenewals = {
        ...checkoutPaymentForRenewals,
        googleOneStorage: googleOneStorage,
      };
    }

    if (
      couponForm.values.discount &&
      couponForm.values.discount >= 1 &&
      couponVerificationStatus === "verified"
    ) {
      const upperCasedCoupon = trimString(
        couponForm.values.coupon,
      ).toUpperCase();
      checkoutPayloadForNewPayment = {
        ...checkoutPayloadForNewPayment,
        coupon: upperCasedCoupon,
      };
      checkoutPaymentForRenewals = {
        ...checkoutPaymentForRenewals,
        coupon: upperCasedCoupon,
      };
    }

    const rCheckout = await PerformRequest<
      NonPaginatedResponse<{ paymentLink: string; subscriptionId: string; id: string }>
    >({
      method: "POST",
      route: isRenewalPayment
        ? Endpoints.GetCheckoutUserForRenewal()
        : Endpoints.GetCreateUserCheckout(),
      data: isRenewalPayment
        ? checkoutPaymentForRenewals
        : checkoutPayloadForNewPayment,
      callback: () => {
        StopPaymentLoading();
      },
    });
    StopPaymentLoading();
    if (rCheckout) {
      if (rCheckout.status === 201) {
        const { paymentLink } = rCheckout.data.data;
        if (paymentLink) {
          // Redirect to Dodo Payments hosted checkout
          window.location.href = paymentLink;
        } else {
          DefaultErrorNotification("Payment link not received. Please try again.");
        }
      } else {
        DefaultErrorNotification("Could not create checkout!");
      }
    }
  };

  const isCheckoutAllowed = () => {
    if (context && context.profile) {
      if (context.profile.isVerified) {
        const { coupon } = couponForm.values;

        if (coupon.length === 0) {
          return true;
        } else {
          if (
            (
              [
                "verifying",
                "error",
                "unverified",
              ] as (typeof couponVerificationStatus)[]
            ).includes(couponVerificationStatus)
          ) {
            // COUPON IS EITHER BEING VERIFIED OR IS A FAKE COUPON
            return false;
          } else {
            return true;
          }
        }
      }
    } else {
      return true;
    }
  };
  return (
    <>
      <motion.div
        className={`cart-container`}
        animate={{
          height: height,
          bottom: 0,
        }}
        initial={false}
      >
        {!isSmallScreen || isCartShowing ? (
          <Paper
            shadow={hideShadow ? undefined : "sm"}
            className={`cart-content cart-content-${theme}`}
            radius="lg"
          >
            <div className="flex-col body justify-between">
              <div className="item flex-col">
                <div className="flex-row align-center width-100 justify-between">
                  <Text
                    className="header"
                    c={theme === "dark" ? Colors.White : Colors.GreenSecondary}
                  >
                    Selected Service
                    {selectedServices && selectedServices.length > 1 && "s"}
                  </Text>
                  {isSmallScreen && (
                    <Button
                      variant="filled"
                      onClick={CloseCart}
                      size="sm"
                      color={Colors.BluePrimary}
                      rightSection={<IconChevronDown />}
                    >
                      Hide
                    </Button>
                  )}
                </div>
                <motion.div
                  initial={false}
                  animate={{
                    opacity: showImages ? 1 : 0,
                  }}
                  style={{
                    overflow: "hidden",
                    padding: 0,
                    marginTop: 10,
                  }}
                  className="flex-row align-center services"
                >
                  {selectedServices && selectedServices.length > 0 && (
                    <>
                      {selectedServices?.map((service) => {
                        const image = BrandImages.retrieveImage(service, theme);
                        return (
                          <img
                            className="img"
                            key={service}
                            src={image}
                            alt=""
                          />
                        );
                      })}
                    </>
                  )}
                  {currentSingleService && currentSingleService.length > 0 && (
                    <img
                      className="img"
                      src={assumingCurrentSingleServiceExistsSoImage}
                      alt=""
                    />
                  )}
                </motion.div>
              </div>
              <div className="item flex-col">
                <Text
                  className="header"
                  mt={10}
                  c={theme === "dark" ? Colors.White : Colors.GreenSecondary}
                >
                  Period
                </Text>

                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "repeat(3, 1fr)",
                    gap: "8px",
                    marginBottom: "5px",
                  }}
                >
                  {PurchasePeriods.map((period) => {
                    const isPeriodSelected = selectedPeriod === period.value;
                    return (
                      <div key={period.value}>
                        <Button
                          classNames={{
                            root: "period",
                          }}
                          color={
                            period.value === "1 Year"
                              ? Colors.GreenPrimary
                              : Colors.BluePrimary
                          }
                          radius="xl"
                          size="xs"
                          fullWidth
                          onClick={() => {
                            setSelectedPeriod(period.value);
                          }}
                          variant={isPeriodSelected ? "filled" : "outline"}
                        >
                          <Text
                            fw={period.value === "1 Year" ? 700 : 400}
                            fz={12}
                          >
                            {period.value}
                          </Text>
                        </Button>
                      </div>
                    );
                  })}
                </div>
              </div>
              <Text
                c={
                  selectedPeriod === "1 Year"
                    ? Colors.GreenPrimary
                    : Colors.OrangePrimary
                }
                fw={700}
                my={5}
                fz={15}
              >
                Get 1 Month <strong>FREE </strong>
                When You Subscribe for a Full Year!
              </Text>
              <div className="flex-row align-center">
                <Text fz={13} c={Colors.BluePrimary}>
                  Total Savings
                </Text>
                <Text ml={5} c={Colors.GreenPrimary} fz="lg" fw={500}>
                  ${getFinancialValueFromNumeric(totalSaved)}
                </Text>
              </div>
              <Divider my={4} />
              <div className="item flex-col">
                <Text
                  className="header"
                  mb={-5}
                  c={theme === "dark" ? Colors.White : Colors.BlackPrimary}
                >
                  Total Cost
                </Text>
                <Text className="total">
                  ${getFinancialValueFromNumeric(totalAmount)}
                </Text>
                <Button
                  color={Colors.GreenPrimary}
                  fw={400}
                  disabled={!isSelectedServiceOrBundle}
                  onClick={OpenPaymentModal}
                  h={40}
                  fullWidth
                >
                  Continue
                </Button>
              </div>
            </div>
          </Paper>
        ) : (
          <div
            className={`cart-banner cart-banner-${theme} flex-row align-center justify-between`}
            style={{
              gap: "12px",
              paddingRight: "16px",
              width: "100%",
              overflow: "hidden",
            }}
          >
            <div className="flex-col" style={{ flex: 1, minWidth: 0 }}>
              <Text
                c={theme === "dark" ? Colors.GrayPrimary : Colors.GraySecondary}
                fz={11}
                fw={700}
                tt="uppercase"
                style={{ letterSpacing: "0.5px" }}
              >
                Total Cost
              </Text>
              <Text
                c={theme === "dark" ? Colors.White : Colors.BluePrimary}
                fz={22}
                fw={800}
                style={{
                  letterSpacing: "-0.5px",
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  lineHeight: 1.2,
                }}
              >
                ${getFinancialValueFromNumeric(totalAmount)}
              </Text>
            </div>
            <Button
              size="sm"
              radius="xl"
              h={36}
              px={16}
              color={Colors.GreenPrimary}
              onClick={OpenCart}
              rightSection={<IconChevronUp size={16} stroke={3} />}
              styles={{
                root: {
                  background:
                    theme === "dark"
                      ? "rgba(255, 255, 255, 0.1)"
                      : "rgba(33, 150, 243, 0.1)",
                  color:
                    theme === "dark" ? "#fff" : "var(--mantine-color-blue-6)",
                  border: `1px solid ${
                    theme === "dark"
                      ? "rgba(255,255,255,0.1)"
                      : "rgba(33,150,243,0.2)"
                  }`,
                  flexShrink: 0,
                },
              }}
            >
              View
            </Button>
          </div>
        )}
      </motion.div>
      <Modal
        zIndex={999999}
        centered
        withCloseButton={false}
        opened={isPaymentModalOpened}
        onClose={ClosePaymentModal}
        styles={{
          body: {
            padding: 0,
          },
          content: {
            borderRadius: "20px",
            backgroundColor: "transparent",
            boxShadow: "none",
          },
        }}
      >
        <div className={`checkout-container checkout-${theme}`}>
          <Text
            fz={17}
            c={theme === "dark" ? Colors.White : Colors.BlackPrimary}
            mb={10}
            fw={500}
          >
            Checkout
          </Text>
          <Divider w="100%" mb={10} />
          <Text fz={15} c={Colors.BluePrimary}>
            Services
          </Text>
          {selectedServices && selectedServices.length > 0 && (
            <>
              {selectedServices?.map((service) => {
                const image = BrandImages.retrieveImage(service, theme);
                return <img className="img" key={service} src={image} alt="" />;
              })}
            </>
          )}
          {currentSingleService && currentSingleService.length > 0 && (
            <img
              className="img"
              src={assumingCurrentSingleServiceExistsSoImage}
              alt=""
            />
          )}
          <div className="flex-row align-end">
            <div className="flex-col">
              <Text fz={15} c={Colors.BluePrimary} mt={12}>
                Duration
              </Text>
              <Badge fz={14} h={30} miw={90} fw={400}>
                {selectedPeriod}
              </Badge>
            </div>
            {selectedPeriod === "1 Year" && (
              <Text ml={6} mb={5} c={Colors.GreenPrimary} fw={700}>
                + 1 FREE MONTH!
              </Text>
            )}
          </div>

          <Text fz={15} c={Colors.BluePrimary} mt={12}>
            Total
          </Text>

          {couponVerificationStatus === "verified" && (
            <div className="flex-row align-center">
              <Text
                style={{
                  textDecoration: "line-through",
                }}
                fz={15}
                c={Colors.GraySecondary}
                fw={500}
              >
                ${getFinancialValueFromNumeric(totalAmount)}
              </Text>

              <Text ml={10} c={Colors.GreenPrimary} fw={600}>
                -$
                {getFinancialValueFromNumeric(
                  totalAmount -
                    getDiscountedValue(totalAmount, couponForm.values.discount),
                )}
              </Text>
            </div>
          )}
          <Text fz={24} c={Colors.GreenPrimary} fw={500}>
            $
            {getFinancialValueFromNumeric(
              couponVerificationStatus === "verified"
                ? getDiscountedValue(totalAmount, couponForm.values.discount)
                : totalAmount,
            )}
          </Text>

          <div className="flex-row align-end">
            <input type="text" hidden={true} autoFocus />
            <TextInput
              w="-webkit-fill-available"
              {...couponForm.getInputProps("coupon")}
              onChange={(event) => {
                const value = event.currentTarget.value.toUpperCase();
                couponForm.setFieldValue("coupon", value);
              }}
              label="Coupon (optional)"
              styles={{
                label: {
                  color: theme === "dark" ? Colors.White : Colors.BlackPrimary,
                },
              }}
              description="Do you have a coupon code?"
              placeholder="Enter coupon code"
              spellCheck={false}
              autoFocus={false}
              disabled={couponVerificationStatus === "verified"}
              classNames={{
                input: "coupon-input",
              }}
            />
            {couponVerificationStatus === "verified" ? (
              <ActionIcon
                onClick={() => {
                  setCouponVerificationStatus("unset");
                  couponForm.setFieldValue("discount", 0);
                }}
                h={45}
                w={86}
                ml={10}
              >
                <IconEdit strokeWidth={1.2} />
              </ActionIcon>
            ) : (
              <ActionIcon
                loading={couponVerificationStatus === "verifying"}
                onClick={ValidateCoupon}
                h={45}
                w={86}
                ml={10}
              >
                <IconCheck />
              </ActionIcon>
            )}
          </div>
          <br />
          <br />
          {!context?.profile ? (
            <Text mb={4} fz={14.5} c={Colors.OrangePrimary} fw={600}>
              You must log in to continue!
            </Text>
          ) : (
            <>
              {!context.profile.isVerified && (
                <Text mb={4} fz={14.5} c={Colors.OrangePrimary} fw={600}>
                  You must verify your email first! Check your inbox
                </Text>
              )}
            </>
          )}
          <Button
            color={
              context && context.profile
                ? Colors.GreenPrimary
                : Colors.OrangePrimary
            }
            fullWidth
            h={40}
            // DISABLE BUTTON IF USER IN ENTERING COUPON THAT HAS NOT BEEN VALIDATED
            disabled={!isCheckoutAllowed()}
            onClick={() => {
              if (context && context.profile) {
                InitiatePayment();
              } else {
                localStorage.setItem(
                  "pending_services",
                  JSON.stringify(selectedServices),
                );
                if (
                  selectedServices &&
                  selectedServices.includes("Google One")
                ) {
                  localStorage.setItem(
                    "pending_google_one_storage",
                    JSON.stringify(googleOneStorage),
                  );
                }
                if (
                  selectedServices &&
                  selectedServices.includes("iCloud - Apple Music")
                ) {
                  localStorage.setItem(
                    "pending_iCloud_storage",
                    JSON.stringify(iCloudStorage),
                  );
                }
                localStorage.setItem("pending_period", selectedPeriod);
                navigate("/login?cart=yes");
              }
            }}
            loading={isPaymentLoading}
          >
            {context && context.profile ? "Checkout" : "Login"}
          </Button>
        </div>
      </Modal>
    </>
  );
};

export default MyCartSection;
